import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { setStoredToken } from "../lib/api";
import { useUser } from "../contexts/UserContext";

export default function AuthCallback() {
  const [params] = useSearchParams();
  const { refresh } = useUser();
  const [state, setState] = useState({ status: "loading", message: "" });

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState({ status: "error", message: "Lien invalide : token manquant." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.post("/auth/magic-link/verify", { token });
        if (cancelled) return;
        // Stocke aussi le token en localStorage : sur iOS Safari, le cookie
        // peut être bloqué par ITP. Le Bearer header devient le fallback.
        if (res.data?.session_token) {
          setStoredToken(res.data.session_token);
        }
        setState({ status: "ok", user: res.data });
        // 1) Synchronise le UserContext (sinon <Protected> ne voit pas
        //    encore l'utilisateur et redirige vers /login).
        // 2) Petite attente : sur Safari iOS, le Set-Cookie n'est pas
        //    immédiatement committé après la réponse JSON. Un délai court
        //    avant le full reload garantit que le cookie est en place
        //    pour le prochain GET.
        // 3) window.location.replace() force un cycle complet (vs
        //    react-router navigate) → plus robuste sur Safari iOS / ITP.
        await refresh();
        setTimeout(() => {
          if (!cancelled) window.location.replace("/");
        }, 600);
      } catch (err) {
        if (cancelled) return;
        const detail =
          err?.response?.data?.detail ||
          "Impossible de valider ce lien. Demandez-en un nouveau.";
        setState({ status: "error", message: detail });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params, refresh]);

  return (
    <div
      data-testid="auth-callback-page"
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "#FAFAFA", fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      <div
        className="w-full max-w-md bg-white rounded-xl border p-10 text-center"
        style={{ borderColor: "#E5E5E5", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
      >
        <div className="mb-8" style={{ fontFamily: "'Sora', sans-serif" }}>
          <div className="text-xs tracking-widest uppercase mb-2" style={{ color: "#666" }}>APOLLO</div>
          <div className="text-3xl font-bold leading-tight" style={{ color: "#1A1A1A" }}>
            <span style={{ color: "#F77F00" }}>Break</span> News
          </div>
        </div>

        {state.status === "loading" && (
          <div data-testid="auth-callback-loading">
            <Loader2 className="animate-spin mx-auto mb-4" size={32} color="#F77F00" />
            <p style={{ color: "#1A1A1A" }}>Validation de votre connexion…</p>
          </div>
        )}

        {state.status === "ok" && (
          <div data-testid="auth-callback-success">
            <CheckCircle2 className="mx-auto mb-4" size={40} color="#10B981" />
            <p className="text-lg font-medium mb-2" style={{ color: "#1A1A1A" }}>
              Bienvenue {state.user?.first_name} 👋
            </p>
            <p style={{ color: "#666" }}>Connexion réussie. Redirection…</p>
          </div>
        )}

        {state.status === "error" && (
          <div data-testid="auth-callback-error">
            <AlertCircle className="mx-auto mb-4" size={40} color="#EF4444" />
            <p className="text-lg font-medium mb-2" style={{ color: "#1A1A1A" }}>
              Lien invalide
            </p>
            <p className="mb-6" style={{ color: "#666" }}>{state.message}</p>
            <Link
              to="/"
              data-testid="back-to-login-link"
              className="inline-block px-6 py-3 rounded-full font-medium text-white transition-opacity hover:opacity-90"
              style={{ background: "#F77F00", fontSize: "14px" }}
            >
              Retour à la connexion
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
